import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import _ from 'lodash';
import { ButtonWithPopover, Icon } from '@seeqdev/qomponents';
import { Ace, getAdditionalText, prettyIdentityType } from '@/accessControl/itemAclModal.utilities';
import { ACLPermissionName, HOME_SCREEN_TABS, PERMISSIONS } from '@/main/app.constants';
import { AceCheckbox } from '@/accessControl/AceCheckbox.molecule';
import { sqHomeScreenStore } from '@/core/core.stores';

type AclModalMainTableRowProps = {
  ace: Ace;
  index: number;
  popover: (ace: Ace) => any;
  currentUserId: string;
  userHasManagePermission: boolean;
  setPermissions: (permission: ACLPermissionName, ace: Ace) => void;
  isAclFromDatasource: boolean;
  displayAceEntries: Ace[];
  isItemAncestorNotCorporate: boolean;
  includeLinksAndCorporateMessage: boolean;
};

export const AclModalMainTableRow: React.FunctionComponent<AclModalMainTableRowProps> = ({
  ace,
  index,
  popover,
  currentUserId,
  userHasManagePermission,
  setPermissions,
  isAclFromDatasource,
  displayAceEntries,
  isItemAncestorNotCorporate,
  includeLinksAndCorporateMessage,
}) => {
  const { t } = useTranslation();

  const showEveryoneDefault = (index: number) =>
    index === 0 &&
    !displayAceEntries[0].permissions?.read &&
    isItemAncestorNotCorporate &&
    includeLinksAndCorporateMessage &&
    sqHomeScreenStore.currentTab !== HOME_SCREEN_TABS.CORPORATE;

  return (
    <>
      <div
        data-testid={showEveryoneDefault(index) ? 'everyoneDefaultRow' : 'aceRow'}
        className={classNames(
          showEveryoneDefault(index) ? 'fakeTableRowDivider' : 'fakeTableRow',
          'flexColumnContainer',
          'flexNoGrowNoShrink',
        )}
        key={`fakeTableRow-${index}`}>
        <div data-testid="aclModalRow" className="specAceIdentity text-valign-middle width-450 sq-text-primary">
          {(ace.identity?.isRedacted || !ace.identity?.isEnabled) && (
            <Icon icon="fa-exclamation-triangle" type="danger" testId="aceNotAccessible" />
          )}
          <ButtonWithPopover
            placement="right"
            key={`${ace.id}-${index}`}
            isHoverEnabled
            alignOffset={-10}
            hasArrow
            trigger={
              <span>
                {ace.identity?.name} {getAdditionalText(ace, t, currentUserId)}
              </span>
            }
            data-testid="aclPopover">
            {popover(ace)}
          </ButtonWithPopover>
        </div>
        {ace.identity?.type && (
          <div className="text-valign-middle min-width-70">{prettyIdentityType(ace.identity.type, t)}</div>
        )}
        {_.map(PERMISSIONS, (perm) => (
          <div className="text-center min-width-75" key={`${perm}_${ace.id}`}>
            <AceCheckbox
              name={perm}
              ace={ace}
              setPermissions={setPermissions}
              isAclFromDatasource={isAclFromDatasource}
              displayOnly={!userHasManagePermission}
            />
          </div>
        ))}
      </div>
    </>
  );
};
